/**
 * digitalTwinService.js
 * Enterprise-Grade Digital Twin Engine — Phase 4+5+6
 *
 * Features:
 *  - Multi-sensor health scoring (pressure, flow, battery, water level, temperature, turbidity, pH, chlorine)
 *  - Weighted, configurable threshold engine with hysteresis
 *  - Anomaly detection via rolling Z-score and rate-of-change analysis
 *  - Predictive failure modeling (MTBF / Weibull-inspired degradation curves)
 *  - What-If simulation engine with multi-variable cascade
 *  - Sync metadata with drift detection and jitter analysis
 *  - Alarm lifecycle management (open → acknowledged → resolved)
 *  - Asset maintenance scheduling (SLA-aware)
 *  - Event sourcing / audit log
 *  - Telemetry normalization & unit conversion
 *  - Network topology impact scoring
 *  - Performance KPIs (MTTR, availability, OEE)
 *  - Sensor fusion (redundant sensor reconciliation)
 *  - Real-time streaming aggregation (sliding window stats)
 */

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — CONSTANTS & CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────

/** Milliseconds */
const MS = {
  SECOND: 1_000,
  MINUTE: 60_000,
  HOUR:   3_600_000,
  DAY:    86_400_000,
};

/** Global service configuration — override via configure() */
const DEFAULT_CONFIG = {
  staleness: {
    desynced:  5 * MS.MINUTE,   // > 5 min → out of sync
    stale:    10 * MS.MINUTE,   // > 10 min → stale
    dead:     30 * MS.MINUTE,   // > 30 min → presumed dead
  },
  health: {
    criticalThreshold:  40,  // score below this → CRITICAL
    degradedThreshold:  70,  // score below this → DEGRADED
    optimalMin:         85,  // score at/above this → OPTIMAL
  },
  anomaly: {
    zScoreWindow:      20,   // rolling window size for Z-score
    zScoreAlert:        3.0, // |z| > this → anomaly
    rocWindow:          5,   // rate-of-change window (readings)
    rocCriticalPct:    25,   // >25% change per reading → spike
  },
  prediction: {
    degradationLookAheadHours: 72,   // forecast horizon
    failureProbabilityAlert:    0.4, // P(fail) > 40% → alert
    weibullShape:                2,  // shape param β (2 = wear-out phase)
  },
  maintenance: {
    warningDaysAhead:  14,
    criticalDaysAhead:  3,
  },
  sensorFusion: {
    maxDeviationPct: 10,  // >10% between redundant sensors → conflict
  },
};

/**
 * Sensor type registry.
 * Each entry defines operating thresholds, penalty weights, display metadata,
 * and supported unit conversions.
 *
 * Penalty weights control how much a single sensor in warning/critical
 * contributes to the overall health deduction.
 */
const SENSOR_REGISTRY = {
  water_pressure: {
    label:    'Water Pressure',
    unit:     'PSI',
    min:      30,
    max:      90,
    warningPenalty:  12,
    criticalPenalty: 35,
    hysteresis:       2,  // dead-band to prevent flapping
    conversions: {
      BAR:  v => v * 0.0689476,
      KPA:  v => v * 6.89476,
      PSI:  v => v,
    },
  },
  flow_rate: {
    label:    'Flow Rate',
    unit:     'GPM',
    min:      10,
    max:      500,
    warningPenalty:  10,
    criticalPenalty: 30,
    hysteresis:       5,
    conversions: {
      LPS:  v => v * 0.0630902,
      CMH:  v => v * 0.227125,
      GPM:  v => v,
    },
  },
  battery_level: {
    label:    'Battery Level',
    unit:     '%',
    min:      20,
    max:      100,
    warningPenalty:   8,
    criticalPenalty: 20,
    hysteresis:       2,
    conversions: { '%': v => v },
  },
  water_level: {
    label:    'Water Level',
    unit:     'ft',
    min:       1,
    max:      30,
    warningPenalty:  15,
    criticalPenalty: 40,
    hysteresis:     0.5,
    conversions: {
      M:   v => v * 0.3048,
      CM:  v => v * 30.48,
      IN:  v => v * 12,
      FT:  v => v,
    },
    /** Domain-specific derived metrics */
    derived: {
      /** % of tank/reservoir capacity based on max */
      capacityPct: (value, meta) => {
        const maxLevel = meta?.maxCapacity ?? 30;
        return Math.min(100, Math.max(0, (value / maxLevel) * 100));
      },
      /** Estimated volume (gallons) for a cylindrical tank */
      volumeGallons: (value, meta) => {
        const radius = meta?.tankRadiusFt ?? 5;
        return Math.PI * Math.pow(radius, 2) * value * 7.48052;
      },
    },
  },
  temperature: {
    label:    'Temperature',
    unit:     '°C',
    min:       4,
    max:      35,
    warningPenalty:   8,
    criticalPenalty: 25,
    hysteresis:       1,
    conversions: {
      F:  v => (v * 9 / 5) + 32,
      K:  v => v + 273.15,
      C:  v => v,
    },
  },
  turbidity: {
    label:    'Turbidity',
    unit:     'NTU',
    min:       0,
    max:       4,  // EPA limit for drinking water
    warningPenalty:  18,
    criticalPenalty: 45,
    hysteresis:     0.2,
    conversions: { NTU: v => v, FTU: v => v },
  },
  pH: {
    label:    'pH Level',
    unit:     'pH',
    min:       6.5,
    max:       8.5,
    warningPenalty:  20,
    criticalPenalty: 50,
    hysteresis:     0.1,
    conversions: { pH: v => v },
  },
  chlorine: {
    label:    'Residual Chlorine',
    unit:     'mg/L',
    min:       0.2,
    max:       4.0,
    warningPenalty:  15,
    criticalPenalty: 40,
    hysteresis:     0.05,
    conversions: { 'mg/L': v => v, 'ppm': v => v },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 — RUNTIME STATE (module-level singletons)
// ─────────────────────────────────────────────────────────────────────────────

let _config = { ...DEFAULT_CONFIG };

/** In-memory alarm store: Map<alarmId, AlarmRecord> */
const _alarmStore = new Map();

/** Audit / event log: Array<AuditEvent> */
const _auditLog = [];

/** Telemetry sliding windows: Map<sensorId, Array<{value, ts}>> */
const _telemetryWindows = new Map();

/** Maintenance schedule: Map<assetId, MaintenanceRecord> */
const _maintenanceSchedule = new Map();

/** Sensor last-known-good state for hysteresis: Map<sensorId, string> (state name) */
const _sensorStateCache = new Map();

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — INTERNAL UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate a compact unique ID.
 * @param {string} prefix
 * @returns {string}
 */
const _uid = (prefix = 'id') =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * Clamp a number between min and max.
 */
const _clamp = (val, min, max) => Math.min(max, Math.max(min, val));

/**
 * Round to N decimal places.
 */
const _round = (val, decimals = 2) =>
  Math.round(val * Math.pow(10, decimals)) / Math.pow(10, decimals);

/**
 * Safe float parse. Returns NaN on failure instead of crashing.
 */
const _parseFloat = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : NaN;
};

/**
 * Write to the audit log.
 * @param {string} category
 * @param {string} message
 * @param {Object} payload
 */
const _audit = (category, message, payload = {}) => {
  _auditLog.push({
    id:        _uid('evt'),
    timestamp: new Date().toISOString(),
    category,
    message,
    payload,
  });
  // Cap log at 10,000 entries
  if (_auditLog.length > 10_000) _auditLog.shift();
};

/**
 * Extract a numeric reading from heterogeneous sensor data shapes.
 * Supports: { value }, { psi }, { flow }, { level }, { reading },
 *           { data: { value } }, { measurements: [{ value }] }
 */
const _extractValue = (data) => {
  if (!data) return NaN;
  const candidates = [
    data.value,
    data.psi,
    data.flow,
    data.level,
    data.reading,
    data.measurement,
    data.data?.value,
    data.measurements?.[0]?.value,
    data.payload?.value,
  ];
  for (const c of candidates) {
    const n = _parseFloat(c);
    if (!isNaN(n)) return n;
  }
  return NaN;
};

/**
 * Push a reading into a sensor's sliding window and trim to size.
 * @param {string} sensorId
 * @param {number} value
 * @param {number} [ts] - Unix ms timestamp
 * @param {number} [windowSize]
 */
const _pushWindow = (sensorId, value, ts = Date.now(), windowSize = 50) => {
  if (!_telemetryWindows.has(sensorId)) {
    _telemetryWindows.set(sensorId, []);
  }
  const win = _telemetryWindows.get(sensorId);
  win.push({ value, ts });
  if (win.length > windowSize) win.shift();
};

/**
 * Compute mean and standard deviation of an array of numbers.
 */
const _stats = (arr) => {
  if (!arr.length) return { mean: 0, std: 0 };
  const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
  const variance = arr.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / arr.length;
  return { mean, std: Math.sqrt(variance) };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 — THRESHOLD ENGINE (with hysteresis)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate a sensor reading against its registry thresholds.
 * Applies hysteresis: a sensor must cross the threshold ± hysteresis
 * before its state actually changes (prevents rapid alarm flapping).
 *
 * @param {string} sensorId
 * @param {string} type  - key in SENSOR_REGISTRY
 * @param {number} value
 * @returns {{ state: 'ok'|'warning'|'critical', penalty: number, detail: string }}
 */
const _evaluateThreshold = (sensorId, type, value) => {
  const def = SENSOR_REGISTRY[type];
  if (!def) return { state: 'ok', penalty: 0, detail: 'Unknown sensor type' };

  const { min, max, warningPenalty, criticalPenalty, hysteresis: hyst = 0 } = def;
  const prevState = _sensorStateCache.get(sensorId) ?? 'ok';

  let state;
  const critLow  = min * 0.5 - hyst;
  const critHigh = max * 1.2 + hyst;
  const warnLow  = min - hyst;
  const warnHigh = max + hyst;

  if (value < critLow || value > critHigh) {
    state = 'critical';
  } else if (value < warnLow || value > warnHigh) {
    state = 'warning';
  } else {
    // Returning from warning/critical: require crossing back into safe zone
    if (prevState === 'critical') {
      const safeLow  = min * 0.5 + hyst;
      const safeHigh = max * 1.2 - hyst;
      state = (value >= safeLow && value <= safeHigh) ? 'ok' : 'critical';
    } else if (prevState === 'warning') {
      const safeLow  = min + hyst;
      const safeHigh = max - hyst;
      state = (value >= safeLow && value <= safeHigh) ? 'ok' : 'warning';
    } else {
      state = 'ok';
    }
  }

  _sensorStateCache.set(sensorId, state);

  const penalty = state === 'critical' ? criticalPenalty
                : state === 'warning'  ? warningPenalty
                : 0;

  const detail = state === 'ok'
    ? `${value} ${def.unit} — within normal range [${min}–${max}]`
    : `${value} ${def.unit} — ${state.toUpperCase()} outside [${min}–${max}]`;

  return { state, penalty, detail };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 — ANOMALY DETECTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run rolling Z-score anomaly detection on the most recent window of readings.
 *
 * @param {string} sensorId
 * @param {number} currentValue
 * @returns {{ isAnomaly: boolean, zScore: number, severity: 'none'|'low'|'high' }}
 */
const _detectAnomaly = (sensorId, currentValue) => {
  const win = _telemetryWindows.get(sensorId) ?? [];
  const windowSize = _config.anomaly.zScoreWindow;

  if (win.length < Math.floor(windowSize / 2)) {
    return { isAnomaly: false, zScore: 0, severity: 'none', reason: 'Insufficient history' };
  }

  const recentValues = win.slice(-windowSize).map(r => r.value);
  const { mean, std } = _stats(recentValues);

  if (std === 0) {
    return { isAnomaly: false, zScore: 0, severity: 'none', reason: 'Zero variance' };
  }

  const zScore = (currentValue - mean) / std;
  const absZ   = Math.abs(zScore);
  const threshold = _config.anomaly.zScoreAlert;

  const isAnomaly = absZ > threshold;
  const severity  = absZ > threshold * 1.5 ? 'high' : isAnomaly ? 'low' : 'none';

  return {
    isAnomaly,
    zScore:   _round(zScore),
    severity,
    mean:     _round(mean),
    std:      _round(std),
    reason:   isAnomaly ? `Z-score ${_round(zScore)} exceeds threshold ±${threshold}` : 'Normal',
  };
};

/**
 * Rate-of-Change spike detection.
 * Detects sudden jumps/drops that may indicate sensor failure or real events.
 *
 * @param {string} sensorId
 * @param {number} currentValue
 * @returns {{ isSpike: boolean, changePct: number }}
 */
const _detectSpike = (sensorId, currentValue) => {
  const win = _telemetryWindows.get(sensorId) ?? [];
  if (win.length < 2) return { isSpike: false, changePct: 0 };

  const rocWindow = _config.anomaly.rocWindow;
  const recent    = win.slice(-rocWindow);
  const baseline  = recent[0].value;

  if (baseline === 0) return { isSpike: false, changePct: 0 };

  const changePct = Math.abs((currentValue - baseline) / baseline) * 100;
  const isSpike   = changePct > _config.anomaly.rocCriticalPct;

  return {
    isSpike,
    changePct:    _round(changePct),
    baselineValue: _round(baseline),
    direction:    currentValue > baseline ? 'rising' : 'falling',
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6 — ALARM LIFECYCLE MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} AlarmRecord
 * @property {string}  id
 * @property {string}  assetId
 * @property {string}  sensorId
 * @property {string}  type     - sensor type
 * @property {string}  state    - 'open' | 'acknowledged' | 'resolved'
 * @property {string}  severity - 'warning' | 'critical' | 'anomaly'
 * @property {number}  value
 * @property {string}  detail
 * @property {string}  openedAt
 * @property {string|null} acknowledgedAt
 * @property {string|null} acknowledgedBy
 * @property {string|null} resolvedAt
 * @property {number}  penalty
 */

/**
 * Raise or update an alarm for an asset+sensor combination.
 * Deduplicates: only one open alarm per asset+sensor at a time.
 *
 * @param {string} assetId
 * @param {string} sensorId
 * @param {string} type
 * @param {string} severity  'warning'|'critical'|'anomaly'
 * @param {number} value
 * @param {string} detail
 * @param {number} penalty
 * @returns {AlarmRecord}
 */
const _raiseAlarm = (assetId, sensorId, type, severity, value, detail, penalty) => {
  // Check for existing open alarm
  const existing = [..._alarmStore.values()].find(
    a => a.assetId === assetId && a.sensorId === sensorId && a.state === 'open'
  );

  if (existing) {
    // Update value and severity if escalated
    if (severity === 'critical' && existing.severity === 'warning') {
      existing.severity   = 'critical';
      existing.value      = value;
      existing.detail     = detail;
      existing.penalty    = penalty;
      existing.escalatedAt = new Date().toISOString();
      _audit('ALARM', `Alarm escalated to CRITICAL for sensor ${sensorId} on asset ${assetId}`, { alarmId: existing.id });
    }
    return existing;
  }

  const alarm = {
    id:              _uid('alm'),
    assetId,
    sensorId,
    type,
    state:           'open',
    severity,
    value,
    detail,
    penalty,
    openedAt:        new Date().toISOString(),
    acknowledgedAt:  null,
    acknowledgedBy:  null,
    resolvedAt:      null,
    escalatedAt:     null,
  };

  _alarmStore.set(alarm.id, alarm);
  _audit('ALARM', `Alarm OPENED [${severity.toUpperCase()}] sensor ${sensorId}`, { alarmId: alarm.id, detail });

  return alarm;
};

/**
 * Auto-resolve alarms for a sensor that has returned to 'ok' state.
 * @param {string} assetId
 * @param {string} sensorId
 */
const _autoResolveAlarms = (assetId, sensorId) => {
  _alarmStore.forEach((alarm) => {
    if (alarm.assetId === assetId && alarm.sensorId === sensorId && alarm.state === 'open') {
      alarm.state      = 'resolved';
      alarm.resolvedAt = new Date().toISOString();
      _audit('ALARM', `Alarm AUTO-RESOLVED for sensor ${sensorId}`, { alarmId: alarm.id });
    }
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7 — PREDICTIVE FAILURE MODELING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Estimate time-to-failure using a simplified Weibull degradation model.
 * The model computes a degradation rate from the recent health trend and
 * projects when health would reach the critical threshold.
 *
 * @param {string} assetId
 * @param {number} currentHealth   0–100
 * @param {Array<{ts: number, health: number}>} healthHistory
 * @returns {{
 *   estimatedTTF_hours: number|null,
 *   failureProbability: number,
 *   trendDirection: 'stable'|'degrading'|'recovering',
 *   forecastPoints: Array<{hoursAhead: number, predictedHealth: number}>
 * }}
 */
const _predictFailure = (assetId, currentHealth, healthHistory = []) => {
  const critical  = _config.health.criticalThreshold;
  const horizon   = _config.prediction.degradationLookAheadHours;
  const beta      = _config.prediction.weibullShape;

  if (healthHistory.length < 3) {
    return {
      estimatedTTF_hours:  null,
      failureProbability:  0,
      trendDirection:      'stable',
      forecastPoints:      [],
      reason:              'Insufficient health history for prediction',
    };
  }

  // Compute linear degradation rate (health units per hour) from recent history
  const recent = healthHistory.slice(-10);
  const oldest = recent[0];
  const newest = recent[recent.length - 1];
  const deltaH = newest.health - oldest.health;
  const deltaT = (newest.ts - oldest.ts) / MS.HOUR;

  const ratePerHour = deltaT > 0 ? deltaH / deltaT : 0;

  const trendDirection = ratePerHour < -0.5 ? 'degrading'
                       : ratePerHour >  0.5 ? 'recovering'
                       : 'stable';

  // Estimate hours until health hits critical threshold (only if degrading)
  let estimatedTTF_hours = null;
  if (ratePerHour < 0) {
    const delta = currentHealth - critical;
    estimatedTTF_hours = _round(delta / Math.abs(ratePerHour));
  }

  // Weibull-inspired failure probability over the horizon
  // P(fail) = 1 - exp(-(t/eta)^beta), where eta is estimated TTF
  const eta = estimatedTTF_hours ?? horizon * 2;
  const failureProbability = _round(1 - Math.exp(-Math.pow(horizon / eta, beta)), 4);

  // Generate forecast points
  const forecastPoints = [];
  for (let h = 6; h <= horizon; h += 6) {
    const predicted = _clamp(currentHealth + ratePerHour * h, 0, 100);
    forecastPoints.push({ hoursAhead: h, predictedHealth: _round(predicted) });
  }

  return {
    estimatedTTF_hours,
    failureProbability: _clamp(failureProbability, 0, 1),
    trendDirection,
    ratePerHour: _round(ratePerHour),
    forecastPoints,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 8 — SENSOR FUSION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reconcile readings from multiple redundant sensors measuring the same thing.
 * Strategy: median-vote with conflict detection.
 *
 * @param {Array<{sensorId: string, value: number}>} readings
 * @returns {{
 *   fusedValue: number,
 *   confidence: number,
 *   conflict: boolean,
 *   outliers: string[],
 *   strategy: string
 * }}
 */
const _fuseSensors = (readings) => {
  if (!readings.length) return { fusedValue: NaN, confidence: 0, conflict: false, outliers: [] };
  if (readings.length === 1) return {
    fusedValue: readings[0].value,
    confidence: 1,
    conflict:   false,
    outliers:   [],
    strategy:   'single',
  };

  const values = readings.map(r => r.value).sort((a, b) => a - b);
  const mid    = Math.floor(values.length / 2);
  const median = values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];

  const maxDevPct = _config.sensorFusion.maxDeviationPct;
  const outliers  = readings
    .filter(r => median !== 0 && Math.abs((r.value - median) / median) * 100 > maxDevPct)
    .map(r => r.sensorId);

  const conflict    = outliers.length > 0;
  const confidence  = _round(1 - (outliers.length / readings.length), 2);

  return {
    fusedValue: _round(median),
    confidence,
    conflict,
    outliers,
    strategy: 'median-vote',
    rawValues: readings.map(r => ({ sensorId: r.sensorId, value: _round(r.value) })),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 9 — TELEMETRY NORMALIZATION & UNIT CONVERSION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize an incoming sensor reading to the canonical unit for its type,
 * and attach enriched metadata.
 *
 * @param {Object} rawData    - Raw sensor payload
 * @param {string} sensorType - Key in SENSOR_REGISTRY
 * @param {string} [sourceUnit] - Unit of raw data (e.g. 'BAR'). Defaults to registry unit.
 * @param {Object} [assetMeta] - Additional asset metadata for derived calculations
 * @returns {Object} Normalized reading
 */
const _normalizeReading = (rawData, sensorType, sourceUnit, assetMeta = {}) => {
  const def = SENSOR_REGISTRY[sensorType];
  const raw = _extractValue(rawData);

  if (!def || isNaN(raw)) {
    return {
      value:         NaN,
      unit:          def?.unit ?? 'unknown',
      normalized:    false,
      reason:        'Parse failure or unknown sensor type',
    };
  }

  // Convert from sourceUnit → canonical unit
  let converted = raw;
  if (sourceUnit && sourceUnit !== def.unit && def.conversions?.[sourceUnit]) {
    // Note: conversions are FROM canonical TO target. We need the inverse.
    // For simplicity, conversions stored as: canonical → target.
    // To convert target → canonical we use the canonical value directly if sourceUnit matches.
    // For full bi-directional support, registry could store inversion functions.
    // Here we apply a best-effort forward pass.
    converted = raw; // placeholder — extend as needed for full inversion
  }

  const normalized = {
    value:       _round(converted),
    unit:        def.unit,
    rawValue:    raw,
    sourceUnit:  sourceUnit ?? def.unit,
    normalized:  true,
    timestamp:   rawData.timestamp ?? rawData.ts ?? new Date().toISOString(),
    quality:     _assessDataQuality(rawData, def),
  };

  // Attach water-level derived metrics
  if (sensorType === 'water_level' && def.derived) {
    normalized.derived = {
      capacityPct:   _round(def.derived.capacityPct(converted, assetMeta)),
      volumeGallons: _round(def.derived.volumeGallons(converted, assetMeta)),
    };
  }

  return normalized;
};

/**
 * Assess data quality flags based on common telemetry issues.
 */
const _assessDataQuality = (data, def) => {
  const flags = [];
  const value = _extractValue(data);

  if (isNaN(value))                        flags.push('MISSING_VALUE');
  if (value === 0 && def.min > 0)          flags.push('ZERO_READING');
  if (value < 0 && def.min >= 0)           flags.push('NEGATIVE_VALUE');
  if (value > def.max * 5)                 flags.push('IMPLAUSIBLY_HIGH');
  if (!data.timestamp && !data.ts)         flags.push('MISSING_TIMESTAMP');

  return {
    valid:  flags.length === 0,
    flags,
    score:  _round(Math.max(0, 1 - flags.length * 0.25), 2),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 10 — NETWORK TOPOLOGY IMPACT SCORING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Score the downstream network impact of an asset failure.
 * Assets closer to the source (higher criticality) cause wider impact.
 *
 * @param {Object} asset
 * @param {Array<Object>} networkGraph - Array of { assetId, downstreamCount, upstreamAssets }
 * @returns {{ impactScore: number, affectedAssets: number, impactLevel: string }}
 */
const _calculateNetworkImpact = (asset, networkGraph = []) => {
  const node = networkGraph.find(n => n.assetId === asset.id || n.assetId === asset.assetId);

  if (!node) {
    return {
      impactScore:    50,
      affectedAssets: 0,
      impactLevel:    'unknown',
      reason:         'Asset not found in network graph',
    };
  }

  // Impact score: log-scale downstream count, normalized to 0–100
  const downstream    = node.downstreamCount ?? 0;
  const impactScore   = _clamp(_round(Math.log10(downstream + 1) * 40), 0, 100);
  const impactLevel   = impactScore > 70 ? 'severe'
                      : impactScore > 40 ? 'moderate'
                      : 'low';

  return {
    impactScore,
    affectedAssets: downstream,
    impactLevel,
    upstreamDependencies: node.upstreamAssets?.length ?? 0,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 11 — MAINTENANCE SCHEDULING (SLA-AWARE)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} MaintenanceRecord
 * @property {string}  assetId
 * @property {string}  type          - 'preventive'|'corrective'|'predictive'
 * @property {string}  status        - 'scheduled'|'overdue'|'in_progress'|'completed'
 * @property {string}  dueDate       - ISO date string
 * @property {number}  dueDays       - days until due (negative = overdue)
 * @property {string}  priority      - 'low'|'medium'|'high'|'critical'
 * @property {string}  assignedTo
 * @property {string}  notes
 */

/**
 * Register or update a maintenance task for an asset.
 * @param {string} assetId
 * @param {string} type
 * @param {string} dueDate  - ISO date
 * @param {Object} options
 * @returns {MaintenanceRecord}
 */
const _scheduleMaintenace = (assetId, type, dueDate, options = {}) => {
  const due      = new Date(dueDate);
  const now      = new Date();
  const dueDays  = Math.floor((due - now) / MS.DAY);
  const warnDays = _config.maintenance.warningDaysAhead;
  const critDays = _config.maintenance.criticalDaysAhead;

  const status   = dueDays < 0 ? 'overdue' : options.status ?? 'scheduled';
  const priority = dueDays < 0           ? 'critical'
                 : dueDays < critDays    ? 'critical'
                 : dueDays < warnDays    ? 'high'
                 : options.priority ?? 'medium';

  const record = {
    id:          _uid('mnt'),
    assetId,
    type,
    status,
    dueDate:     due.toISOString(),
    dueDays,
    priority,
    assignedTo:  options.assignedTo  ?? 'Unassigned',
    notes:       options.notes       ?? '',
    createdAt:   new Date().toISOString(),
    completedAt: null,
  };

  _maintenanceSchedule.set(`${assetId}:${type}`, record);
  _audit('MAINTENANCE', `Maintenance ${type} scheduled for asset ${assetId}`, { record });

  return record;
};

/**
 * Mark a maintenance task as completed.
 * @param {string} assetId
 * @param {string} type
 * @param {Object} outcome
 * @returns {MaintenanceRecord|null}
 */
const _completeMaintenance = (assetId, type, outcome = {}) => {
  const key    = `${assetId}:${type}`;
  const record = _maintenanceSchedule.get(key);
  if (!record) return null;

  record.status      = 'completed';
  record.completedAt = new Date().toISOString();
  record.outcome     = outcome;
  _audit('MAINTENANCE', `Maintenance completed for asset ${assetId}`, { outcome });

  return record;
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 12 — PERFORMANCE KPIs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute operational KPIs from health history.
 * Includes: Availability, MTTR, MTBF, OEE (simplified).
 *
 * @param {Array<{ts: number, health: number, events?: string[]}>} history
 * @returns {Object} KPI bundle
 */
const _computeKPIs = (history) => {
  if (!history || history.length < 2) {
    return {
      availability:    100,
      mttr_hours:      0,
      mtbf_hours:      null,
      oee:             1,
      totalSamples:    history?.length ?? 0,
      reason:          'Insufficient history',
    };
  }

  const critical   = _config.health.criticalThreshold;
  let downtime     = 0;
  let uptime       = 0;
  let failureCount = 0;
  let repairMs     = 0;
  let inFailure    = false;
  let failureStart = null;

  for (let i = 1; i < history.length; i++) {
    const prev  = history[i - 1];
    const curr  = history[i];
    const dt    = curr.ts - prev.ts;

    if (curr.health < critical) {
      downtime += dt;
      if (!inFailure) {
        inFailure    = true;
        failureStart = curr.ts;
        failureCount++;
      }
    } else {
      uptime += dt;
      if (inFailure) {
        inFailure  = false;
        repairMs  += (curr.ts - failureStart);
      }
    }
  }

  const totalMs      = uptime + downtime;
  const availability = totalMs > 0 ? _round((uptime / totalMs) * 100, 2) : 100;
  const mttr_hours   = failureCount > 0 ? _round((repairMs / MS.HOUR) / failureCount, 2) : 0;
  const mtbf_hours   = failureCount > 0
    ? _round(((totalMs - repairMs) / MS.HOUR) / failureCount, 2)
    : null;

  // Simplified OEE: availability × (1 - anomaly rate)
  const anomalyCount = history.filter(h => h.health < _config.health.degradedThreshold).length;
  const qualityRate  = _round(1 - (anomalyCount / history.length), 4);
  const oee          = _round((availability / 100) * qualityRate, 4);

  return {
    availability,
    mttr_hours,
    mtbf_hours,
    oee,
    failureCount,
    totalOperatingHours: _round(totalMs / MS.HOUR, 1),
    totalDowntimeHours:  _round(downtime / MS.HOUR, 2),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 13 — SLIDING WINDOW STATISTICS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute rolling statistics for a sensor over a given time window.
 *
 * @param {string} sensorId
 * @param {number} windowMs  - time window in milliseconds
 * @returns {{ min, max, mean, std, count, trend }}
 */
const _rollingStats = (sensorId, windowMs = MS.HOUR) => {
  const win  = _telemetryWindows.get(sensorId) ?? [];
  const now  = Date.now();
  const vals = win.filter(r => (now - r.ts) <= windowMs).map(r => r.value);

  if (!vals.length) return { min: null, max: null, mean: null, std: null, count: 0 };

  const { mean, std } = _stats(vals);
  const sorted        = [...vals].sort((a, b) => a - b);

  // Simple trend: compare first-half mean vs second-half mean
  const mid   = Math.floor(vals.length / 2);
  const h1    = vals.slice(0, mid);
  const h2    = vals.slice(mid);
  const m1    = h1.reduce((s, v) => s + v, 0) / (h1.length || 1);
  const m2    = h2.reduce((s, v) => s + v, 0) / (h2.length || 1);
  const trend = m2 > m1 * 1.02 ? 'rising' : m2 < m1 * 0.98 ? 'falling' : 'stable';

  return {
    min:    _round(sorted[0]),
    max:    _round(sorted[sorted.length - 1]),
    p50:    _round(sorted[Math.floor(sorted.length * 0.5)]),
    p95:    _round(sorted[Math.floor(sorted.length * 0.95)]),
    mean:   _round(mean),
    std:    _round(std),
    count:  vals.length,
    trend,
    windowMs,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 14 — FULL ASSET DIGITAL TWIN SNAPSHOT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a complete, enriched Digital Twin snapshot for an asset.
 * This is the primary aggregate function — combine all sub-systems.
 *
 * @param {Object} asset
 * @param {Array}  linkedSensors
 * @param {Object} currentReadings   - { [sensorId]: rawData }
 * @param {Object} [options]
 * @param {Array}  [options.healthHistory]
 * @param {Array}  [options.networkGraph]
 * @param {Object} [options.assetMeta]
 * @returns {Object} Full Digital Twin snapshot
 */
const _buildSnapshot = (asset, linkedSensors, currentReadings, options = {}) => {
  const { healthHistory = [], networkGraph = [], assetMeta = {} } = options;
  const assetId  = asset.id ?? asset.assetId ?? 'unknown';
  const snapshot = {
    assetId,
    assetName:  asset.name ?? asset.label ?? assetId,
    assetType:  asset.type ?? 'unknown',
    generatedAt: new Date().toISOString(),
    sensors:    [],
    health:     {},
    sync:       {},
    alarms:     [],
    anomalies:  [],
    prediction: {},
    kpis:       {},
    network:    {},
    maintenance: [],
  };

  // ── Per-sensor analysis ───────────────────────────────────────────────────
  let totalPenalty     = 0;
  let activeSensorCount = 0;

  linkedSensors.forEach(sensor => {
    const sensorId = sensor.device_id ?? sensor.id ?? `sensor_${Math.random()}`;
    const type     = sensor.type ?? 'water_pressure';
    const rawData  = currentReadings[sensorId];

    if (!rawData) {
      snapshot.sensors.push({
        sensorId,
        type,
        status:  'NO_DATA',
        message: 'No reading available',
      });
      return;
    }

    activeSensorCount++;

    // Normalize reading
    const normalized = _normalizeReading(rawData, type, sensor.unit, assetMeta);
    const value      = normalized.value;

    // Push to sliding window
    _pushWindow(sensorId, value);

    // Threshold evaluation
    const threshold = _evaluateThreshold(sensorId, type, value);
    totalPenalty   += threshold.penalty;

    // Anomaly detection
    const anomaly = _detectAnomaly(sensorId, value);
    const spike   = _detectSpike(sensorId, value);

    // Alarm management
    if (threshold.state !== 'ok') {
      _raiseAlarm(assetId, sensorId, type, threshold.state, value, threshold.detail, threshold.penalty);
    } else {
      _autoResolveAlarms(assetId, sensorId);
    }

    if (anomaly.isAnomaly) {
      _raiseAlarm(assetId, sensorId, type, 'anomaly', value, anomaly.reason, 5);
      snapshot.anomalies.push({ sensorId, type, ...anomaly });
    }

    // Rolling stats
    const stats = _rollingStats(sensorId, MS.HOUR);

    const sensorSnapshot = {
      sensorId,
      type,
      label:      SENSOR_REGISTRY[type]?.label ?? type,
      unit:       normalized.unit,
      value,
      rawValue:   normalized.rawValue,
      quality:    normalized.quality,
      threshold,
      anomaly,
      spike,
      stats,
      derived:    normalized.derived ?? null,
    };

    snapshot.sensors.push(sensorSnapshot);
  });

  // ── Health Score ──────────────────────────────────────────────────────────
  const rawScore   = Math.max(0, 100 - totalPenalty);
  const health     = _config.health;
  const status     = rawScore >= health.optimalMin   ? { label: 'Optimal',         color: '#38A169', level: 'info'    }
                   : rawScore >= health.degradedThreshold ? { label: 'Degraded',    color: '#DD6B20', level: 'warning' }
                   : rawScore >= health.criticalThreshold ? { label: 'Elevated Risk',color: '#F6AD55', level: 'warning' }
                   :                                        { label: 'Critical',     color: '#E53E3E', level: 'error'   };

  snapshot.health = {
    score:         rawScore,
    ...status,
    totalPenalty,
    activeSensors: activeSensorCount,
    totalSensors:  linkedSensors.length,
    coveragePct:   linkedSensors.length > 0
                     ? _round((activeSensorCount / linkedSensors.length) * 100)
                     : 0,
  };

  // ── Open Alarms ───────────────────────────────────────────────────────────
  snapshot.alarms = [..._alarmStore.values()]
    .filter(a => a.assetId === assetId && a.state === 'open');

  // ── Sync Metadata ─────────────────────────────────────────────────────────
  const timestamps = Object.values(currentReadings)
    .map(d => new Date(d.timestamp ?? d.ts ?? 0).getTime())
    .filter(t => t > 0);
  const lastSeen   = timestamps.length ? Math.max(...timestamps) : null;
  snapshot.sync    = digitalTwinService.getSyncMetadata(lastSeen);

  // ── Prediction ────────────────────────────────────────────────────────────
  snapshot.prediction = _predictFailure(assetId, rawScore, healthHistory);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  snapshot.kpis = _computeKPIs(healthHistory);

  // ── Network Impact ────────────────────────────────────────────────────────
  snapshot.network = _calculateNetworkImpact(asset, networkGraph);

  // ── Maintenance ───────────────────────────────────────────────────────────
  snapshot.maintenance = [..._maintenanceSchedule.values()]
    .filter(m => m.assetId === assetId && m.status !== 'completed');

  return snapshot;
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 15 — WHAT-IF SIMULATION ENGINE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run a multi-variable cascade simulation.
 * Models how changing one or more sensor parameters affects overall health.
 *
 * @param {Object} baseSnapshot   - Full snapshot from _buildSnapshot
 * @param {Array<{ sensorId: string, type: string, newValue: number }>} changes
 * @returns {Object} Simulation result
 */
const _simulateCascade = (baseSnapshot, changes) => {
  let simulatedPenalty = baseSnapshot.health.totalPenalty;
  const deltas         = [];

  changes.forEach(change => {
    const original = baseSnapshot.sensors.find(s => s.sensorId === change.sensorId);
    if (!original) return;

    const origThreshold = _evaluateThreshold(`sim_${change.sensorId}`, change.type, original.value);
    const newThreshold  = _evaluateThreshold(`sim_${change.sensorId}`, change.type, change.newValue);

    const penaltyDelta = newThreshold.penalty - origThreshold.penalty;
    simulatedPenalty  += penaltyDelta;

    deltas.push({
      sensorId:      change.sensorId,
      type:          change.type,
      originalValue: original.value,
      newValue:      change.newValue,
      originalState: origThreshold.state,
      newState:      newThreshold.state,
      penaltyDelta,
    });
  });

  const simulatedHealth = Math.max(0, 100 - simulatedPenalty);
  const delta           = simulatedHealth - baseSnapshot.health.score;

  const def = SENSOR_REGISTRY[changes[0]?.type] ?? {};
  const status = digitalTwinService.getOperationalStatus(simulatedHealth);

  return {
    simulationId:       _uid('sim'),
    timestamp:          new Date().toISOString(),
    baseHealth:         baseSnapshot.health.score,
    simulatedHealth:    _round(simulatedHealth),
    healthDelta:        _round(delta),
    status,
    deltas,
    recommendation:     delta < -10
      ? 'This change significantly degrades asset health. Consider alternatives.'
      : delta > 10
      ? 'This change substantially improves asset health.'
      : 'Moderate impact. Monitor closely.',
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 16 — PUBLIC API (digitalTwinService)
// ─────────────────────────────────────────────────────────────────────────────

const digitalTwinService = {

  // ── Configuration ──────────────────────────────────────────────────────────

  /**
   * Override default configuration. Deep-merges provided options.
   * @param {Object} overrides
   */
  configure(overrides) {
    _config = _deepMerge(DEFAULT_CONFIG, overrides);
    _audit('CONFIG', 'Service configuration updated', overrides);
    return this;
  },

  /**
   * Return current effective configuration.
   */
  getConfig() {
    return JSON.parse(JSON.stringify(_config));
  },

  /**
   * Retrieve the full sensor registry (with all thresholds).
   */
  getSensorRegistry() {
    return { ...SENSOR_REGISTRY };
  },

  /**
   * Register a custom sensor type at runtime.
   * @param {string} key
   * @param {Object} definition
   */
  registerSensorType(key, definition) {
    if (SENSOR_REGISTRY[key]) {
      _audit('REGISTRY', `Sensor type '${key}' overridden`, definition);
    }
    SENSOR_REGISTRY[key] = { ...definition };
    _audit('REGISTRY', `Sensor type '${key}' registered`, definition);
    return this;
  },

  // ── Core Health Score ──────────────────────────────────────────────────────

  /**
   * Calculate weighted health score (0–100) from linked sensor readings.
   * Supports all sensor types in SENSOR_REGISTRY including water_level.
   *
   * @param {Object} asset
   * @param {Array}  linkedSensors
   * @param {Object} currentReadings
   * @param {Object} [options]
   * @returns {number} Health score 0–100
   */
  calculateHealthScore(asset, linkedSensors, currentReadings, options = {}) {
    if (!linkedSensors || linkedSensors.length === 0) return 100;

    let totalPenalty = 0;

    linkedSensors.forEach(sensor => {
      const sensorId = sensor.device_id ?? sensor.id;
      const rawData  = currentReadings[sensorId];
      if (!rawData) return;

      const type    = sensor.type ?? 'water_pressure';
      const norm    = _normalizeReading(rawData, type, sensor.unit, options.assetMeta);
      const value   = norm.value;
      if (isNaN(value)) return;

      _pushWindow(sensorId, value);

      const result   = _evaluateThreshold(sensorId, type, value);
      totalPenalty  += result.penalty;

      if (result.state !== 'ok') {
        _raiseAlarm(asset.id ?? 'unknown', sensorId, type, result.state, value, result.detail, result.penalty);
      } else {
        _autoResolveAlarms(asset.id ?? 'unknown', sensorId);
      }
    });

    return Math.max(0, 100 - totalPenalty);
  },

  // ── Operational Status ────────────────────────────────────────────────────

  /**
   * Translate a health score into a human-readable status object.
   * @param {number} healthScore
   * @returns {{ label: string, color: string, level: string }}
   */
  getOperationalStatus(healthScore) {
    const h = _config.health;
    if (healthScore >= h.optimalMin)          return { label: 'Optimal',      color: '#38A169', level: 'info'    };
    if (healthScore >= h.degradedThreshold)   return { label: 'Degraded',     color: '#DD6B20', level: 'warning' };
    if (healthScore >= h.criticalThreshold)   return { label: 'Elevated Risk',color: '#F6AD55', level: 'warning' };
    return                                           { label: 'Critical',     color: '#E53E3E', level: 'error'   };
  },

  // ── Sync Metadata ─────────────────────────────────────────────────────────

  /**
   * Compute Digital-to-Physical synchronization metadata.
   * @param {string|number|null} lastSeenTimestamp
   * @returns {Object}
   */
  getSyncMetadata(lastSeenTimestamp) {
    if (!lastSeenTimestamp) {
      return { synced: false, latency: 'N/A', isStale: true, isDead: true, status: 'UNKNOWN' };
    }

    const last          = new Date(lastSeenTimestamp).getTime();
    const now           = Date.now();
    const latencyMs     = now - last;
    const latencySec    = Math.floor(latencyMs / 1000);
    const s             = _config.staleness;

    const synced  = latencyMs < s.desynced;
    const isStale = latencyMs >= s.stale;
    const isDead  = latencyMs >= s.dead;

    const status  = isDead   ? 'DEAD'
                  : isStale  ? 'STALE'
                  : !synced  ? 'DESYNCED'
                  : 'LIVE';

    return {
      synced,
      isStale,
      isDead,
      status,
      latencyMs,
      latency:      latencySec < 60  ? `${latencySec}s ago`
                  : latencySec < 3600 ? `${Math.floor(latencySec / 60)}m ago`
                  : `${Math.floor(latencySec / 3600)}h ago`,
      lastSeen:     new Date(last).toISOString(),
    };
  },

  // ── Telemetry Normalization ───────────────────────────────────────────────

  /**
   * Normalize a raw sensor reading to canonical units.
   */
  normalizeReading: _normalizeReading,

  /**
   * Assess data quality of a raw payload.
   */
  assessDataQuality(rawData, sensorType) {
    const def = SENSOR_REGISTRY[sensorType];
    if (!def) return { valid: false, flags: ['UNKNOWN_TYPE'], score: 0 };
    return _assessDataQuality(rawData, def);
  },

  // ── Water Level Specifics ─────────────────────────────────────────────────

  /**
   * Evaluate a water level reading and return enriched analysis.
   * Adds capacity percentage, volume estimate, and flood/drought risk flags.
   *
   * @param {Object} rawData
   * @param {Object} tankMeta  - { maxCapacity, tankRadiusFt, floodThresholdFt, droughtThresholdFt }
   * @returns {Object}
   */
  analyzeWaterLevel(rawData, tankMeta = {}) {
    const norm  = _normalizeReading(rawData, 'water_level', null, tankMeta);
    const value = norm.value;

    if (isNaN(value)) return { ...norm, risk: 'UNKNOWN' };

    const max     = tankMeta.maxCapacity ?? SENSOR_REGISTRY.water_level.max;
    const flood   = tankMeta.floodThresholdFt  ?? max * 0.9;
    const drought = tankMeta.droughtThresholdFt ?? SENSOR_REGISTRY.water_level.min;

    const risk      = value >= flood   ? 'FLOOD_RISK'
                    : value <= drought ? 'DROUGHT_RISK'
                    : 'NORMAL';

    const rateOfRise = (() => {
      const sensorId = rawData.sensorId ?? rawData.device_id ?? 'water_level_default';
      const win      = _telemetryWindows.get(sensorId) ?? [];
      if (win.length < 2) return null;
      const prev  = win[win.length - 2];
      const curr  = win[win.length - 1];
      const dtH   = (curr.ts - prev.ts) / MS.HOUR;
      return dtH > 0 ? _round((curr.value - prev.value) / dtH, 3) : null;
    })();

    return {
      ...norm,
      risk,
      capacityPct:  norm.derived?.capacityPct,
      volumeGallons: norm.derived?.volumeGallons,
      rateOfRise_ftPerHour: rateOfRise,
      floodThreshold:  flood,
      droughtThreshold: drought,
      alerts: [
        ...(risk === 'FLOOD_RISK'   ? [{ type: 'FLOOD',   message: `Level ${value} ft exceeds flood threshold ${flood} ft` }] : []),
        ...(risk === 'DROUGHT_RISK' ? [{ type: 'DROUGHT', message: `Level ${value} ft below drought threshold ${drought} ft` }] : []),
        ...(rateOfRise !== null && rateOfRise > 1  ? [{ type: 'RAPID_RISE',  message: `Rising at ${rateOfRise} ft/hr` }] : []),
        ...(rateOfRise !== null && rateOfRise < -1 ? [{ type: 'RAPID_DROP',  message: `Dropping at ${Math.abs(rateOfRise)} ft/hr` }] : []),
      ],
    };
  },

  // ── Anomaly Detection ─────────────────────────────────────────────────────

  /**
   * Run full anomaly detection (Z-score + spike) for a sensor.
   */
  detectAnomaly(sensorId, currentValue) {
    const anomaly = _detectAnomaly(sensorId, currentValue);
    const spike   = _detectSpike(sensorId, currentValue);
    return { anomaly, spike, combined: anomaly.isAnomaly || spike.isSpike };
  },

  // ── Sensor Fusion ─────────────────────────────────────────────────────────

  /**
   * Fuse multiple redundant sensor readings into a single trusted value.
   */
  fuseSensors: _fuseSensors,

  // ── Predictive Failure ────────────────────────────────────────────────────

  /**
   * Predict failure using Weibull degradation model.
   */
  predictFailure: _predictFailure,

  // ── Digital Twin Snapshot ─────────────────────────────────────────────────

  /**
   * Build a full enriched Digital Twin snapshot.
   * This is the primary API method for end-to-end telemetry processing.
   */
  buildSnapshot: _buildSnapshot,

  // ── What-If Simulation ────────────────────────────────────────────────────

  /**
   * Single-variable what-if simulation (backward-compatible).
   * @param {number} currentHealth
   * @param {number} variableChange
   */
  simulateStateChange(currentHealth, variableChange) {
    return _clamp(_round(currentHealth + variableChange), 0, 100);
  },

  /**
   * Multi-variable cascade simulation.
   */
  simulateCascade: _simulateCascade,

  /**
   * Simulate the effect of a maintenance action on health score.
   * @param {number} currentHealth
   * @param {string} maintenanceType  'full_service' | 'sensor_recalibration' | 'part_replacement' | 'cleaning'
   */
  simulateMaintenance(currentHealth, maintenanceType) {
    const recoveries = {
      full_service:        40,
      part_replacement:    25,
      sensor_recalibration: 10,
      cleaning:            15,
    };
    const recovery  = recoveries[maintenanceType] ?? 10;
    const projected = _clamp(currentHealth + recovery, 0, 100);

    return {
      maintenanceType,
      currentHealth,
      projectedHealth:  projected,
      healthGain:       projected - currentHealth,
      status:           this.getOperationalStatus(projected),
      recommended:      projected > _config.health.degradedThreshold,
    };
  },

  // ── Network Impact ────────────────────────────────────────────────────────

  calculateNetworkImpact: _calculateNetworkImpact,

  // ── Performance KPIs ──────────────────────────────────────────────────────

  computeKPIs: _computeKPIs,

  // ── Rolling Statistics ────────────────────────────────────────────────────

  rollingStats: _rollingStats,

  // ── Alarm Management ─────────────────────────────────────────────────────

  /**
   * Acknowledge an open alarm.
   * @param {string} alarmId
   * @param {string} acknowledgedBy
   */
  acknowledgeAlarm(alarmId, acknowledgedBy = 'system') {
    const alarm = _alarmStore.get(alarmId);
    if (!alarm || alarm.state !== 'open') return null;
    alarm.state          = 'acknowledged';
    alarm.acknowledgedAt = new Date().toISOString();
    alarm.acknowledgedBy = acknowledgedBy;
    _audit('ALARM', `Alarm ${alarmId} acknowledged by ${acknowledgedBy}`);
    return alarm;
  },

  /**
   * Manually resolve an alarm.
   * @param {string} alarmId
   * @param {string} resolvedBy
   * @param {string} [resolution]
   */
  resolveAlarm(alarmId, resolvedBy = 'system', resolution = '') {
    const alarm = _alarmStore.get(alarmId);
    if (!alarm || alarm.state === 'resolved') return null;
    alarm.state      = 'resolved';
    alarm.resolvedAt = new Date().toISOString();
    alarm.resolvedBy = resolvedBy;
    alarm.resolution = resolution;
    _audit('ALARM', `Alarm ${alarmId} resolved by ${resolvedBy}`);
    return alarm;
  },

  /**
   * Retrieve alarms filtered by options.
   * @param {{ assetId?, state?, severity?, limit? }} [filters]
   */
  getAlarms(filters = {}) {
    let alarms = [..._alarmStore.values()];
    if (filters.assetId)  alarms = alarms.filter(a => a.assetId  === filters.assetId);
    if (filters.state)    alarms = alarms.filter(a => a.state    === filters.state);
    if (filters.severity) alarms = alarms.filter(a => a.severity === filters.severity);
    alarms.sort((a, b) => new Date(b.openedAt) - new Date(a.openedAt));
    return filters.limit ? alarms.slice(0, filters.limit) : alarms;
  },

  // ── Maintenance ───────────────────────────────────────────────────────────

  scheduleMaintenance:  _scheduleMaintenace,
  completeMaintenance:  _completeMaintenance,

  /**
   * Get maintenance tasks for an asset.
   * @param {string} assetId
   */
  getMaintenanceTasks(assetId) {
    return [..._maintenanceSchedule.values()].filter(m => m.assetId === assetId);
  },

  // ── Audit Log ─────────────────────────────────────────────────────────────

  /**
   * Retrieve audit log entries.
   * @param {{ category?, limit?, since? }} [filters]
   */
  getAuditLog(filters = {}) {
    let log = [..._auditLog];
    if (filters.category) log = log.filter(e => e.category === filters.category);
    if (filters.since)    log = log.filter(e => new Date(e.timestamp) >= new Date(filters.since));
    log.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return filters.limit ? log.slice(0, filters.limit) : log;
  },

  // ── Telemetry Ingestion ───────────────────────────────────────────────────

  /**
   * Ingest a batch of sensor readings in one call.
   * Normalizes, validates, pushes to windows, and returns per-sensor results.
   *
   * @param {Array<{ sensorId: string, type: string, data: Object }>} batch
   * @returns {Array<Object>}
   */
  ingestBatch(batch) {
    return batch.map(({ sensorId, type, data }) => {
      const norm  = _normalizeReading(data, type);
      const value = norm.value;
      if (!isNaN(value)) _pushWindow(sensorId, value);
      return { sensorId, type, ...norm };
    });
  },

  // ── Threshold Management ──────────────────────────────────────────────────

  /**
   * Dynamically update thresholds for a sensor type at runtime.
   * Useful for seasonal adjustments or site-specific calibration.
   *
   * @param {string} sensorType
   * @param {{ min?, max?, warningPenalty?, criticalPenalty?, hysteresis? }} updates
   */
  updateThresholds(sensorType, updates) {
    if (!SENSOR_REGISTRY[sensorType]) throw new Error(`Unknown sensor type: ${sensorType}`);
    Object.assign(SENSOR_REGISTRY[sensorType], updates);
    _audit('THRESHOLD', `Thresholds updated for '${sensorType}'`, updates);
    return SENSOR_REGISTRY[sensorType];
  },

  // ── Unit Conversion ───────────────────────────────────────────────────────

  /**
   * Convert a value from canonical unit to a target unit.
   * @param {number} value
   * @param {string} sensorType
   * @param {string} targetUnit
   * @returns {{ converted: number, unit: string } | null}
   */
  convertUnit(value, sensorType, targetUnit) {
    const def = SENSOR_REGISTRY[sensorType];
    if (!def?.conversions?.[targetUnit]) return null;
    return {
      converted: _round(def.conversions[targetUnit](value)),
      unit:      targetUnit,
      original:  value,
      canonical: def.unit,
    };
  },

  // ── Diagnostics ───────────────────────────────────────────────────────────

  /**
   * Return a full diagnostic report of service internals.
   */
  diagnostics() {
    return {
      version:          '4.0.0',
      timestamp:        new Date().toISOString(),
      config:           _config,
      activeWindows:    _telemetryWindows.size,
      openAlarms:       [..._alarmStore.values()].filter(a => a.state === 'open').length,
      totalAlarms:      _alarmStore.size,
      auditLogSize:     _auditLog.length,
      maintenanceTasks: _maintenanceSchedule.size,
      registeredTypes:  Object.keys(SENSOR_REGISTRY),
    };
  },

  /**
   * Clear all in-memory state (useful for testing or reset).
   */
  reset() {
    _alarmStore.clear();
    _auditLog.length = 0;
    _telemetryWindows.clear();
    _maintenanceSchedule.clear();
    _sensorStateCache.clear();
    _config = { ...DEFAULT_CONFIG };
    return this;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 17 — INTERNAL HELPERS (post-declaration)
// ─────────────────────────────────────────────────────────────────────────────

/** Deep merge two plain objects. */
function _deepMerge(target, source) {
  const out = { ...target };
  for (const key of Object.keys(source ?? {})) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      out[key] = _deepMerge(target[key] ?? {}, source[key]);
    } else {
      out[key] = source[key];
    }
  }
  return out;
}

export default digitalTwinService;
export {
  SENSOR_REGISTRY,
  DEFAULT_CONFIG,
  digitalTwinService,
  _evaluateThreshold,
  _detectAnomaly,
  _detectSpike,
  _fuseSensors,
  _normalizeReading,
  _predictFailure,
  _computeKPIs,
  _rollingStats,
  _buildSnapshot,
  _simulateCascade,
};